import React, { useEffect, useState } from "react";
import { Hash, Plus, KanbanSquare, Code2, Settings, LogOut, Users, ChevronDown, Check, Bug, X, Search, LayoutGrid, PenTool, FileText } from "lucide-react";
import Avatar from "@/components/Avatar";
import { trace, traceEnabled } from "@/lib/trace";
import QedaraLogo from "@/components/QedaraLogo";
import { brandName } from "@/lib/branding";
import { useFileUrl } from "@/lib/fileAccess";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { roleLabel } from "@/lib/labels";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

function Section({ label, children, action }) {
  return (
    <div className="px-3 mt-5">
      <div className="flex items-center justify-between mb-2">
        <span className="label-tiny">{label}</span>
        {action}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

// A click only fires when press and release land on the same element — or, if
// they do not, on their nearest common ancestor, which here has no handler. The
// report is that the first click highlights the row and selects nothing, and
// the trace shows no `choose` at all for it, so the press is arriving and the
// click is not. This records both ends of the gesture and what moved between.
let navSeq = 0;
let sidebarRenders = 0;

/** A stable id for a DOM node, stamped on the node itself. If React replaces
 *  the node on a re-render the replacement has no stamp and gets a new id —
 *  which is precisely the thing that would break a click in two. */
function nodeId(el) {
  if (!el || !el.dataset) return null;
  if (!el.dataset.vsNode) el.dataset.vsNode = `n${++navSeq}`;
  return el.dataset.vsNode;
}

function traceGesture(phase, testId, event, extra) {
  if (!traceEnabled()) return;
  const el = event.currentTarget;
  const box = el?.getBoundingClientRect?.();
  const under = document.elementFromPoint?.(event.clientX, event.clientY);
  trace(`nav:${phase}`, {
    id: testId,
    node: nodeId(el),
    top: box ? Math.round(box.top) : null,
    pointer: [Math.round(event.clientX), Math.round(event.clientY)],
    // What is under the finger right now: if the row moved between press and
    // release, this names the row that took its place.
    under: under?.closest?.("[data-testid]")?.dataset?.testid || under?.tagName || null,
    renders: sidebarRenders,
    ...(extra || {}),
  });
}

function NavItem({ active, onClick, icon: Icon, iconNode, color, children, badge, badge2, badge2Title, badge2TestId, testId, ariaLabel }) {
  const downRef = React.useRef(null);
  return (
    <button
      data-testid={testId}
      aria-current={active ? "page" : undefined}
      aria-label={ariaLabel}
      onPointerDown={(e) => {
        downRef.current = {
          node: nodeId(e.currentTarget),
          top: Math.round(e.currentTarget.getBoundingClientRect().top),
          renders: sidebarRenders,
          at: Date.now(),
        };
        traceGesture("down", testId, e);
      }}
      onPointerUp={(e) => {
        const d = downRef.current;
        traceGesture("up", testId, e, {
          // The three ways a gesture comes apart: the node was swapped, the row
          // moved out from under the finger, or the press was simply long.
          sameNode: d ? d.node === nodeId(e.currentTarget) : null,
          movedPx: d ? Math.round(e.currentTarget.getBoundingClientRect().top) - d.top : null,
          rendersSinceDown: d ? sidebarRenders - d.renders : null,
          heldMs: d ? Date.now() - d.at : null,
        });
      }}
      onClick={(e) => { traceGesture("click", testId, e); onClick?.(e); }}
      className={`w-full text-left flex items-center gap-2 px-2 py-1.5 text-sm transition-colors ${
        active
          ? "bg-fg/8 text-fg border-l-2 border-line -ml-[2px] pl-[6px]"
          : "text-fg/70 hover:bg-fg/5 hover:text-fg"
      }`}
    >
      {iconNode ? iconNode : Icon && <Icon className="w-4 h-4 flex-shrink-0" style={color ? { color } : undefined} />}
      <span className={`truncate flex-1 ${badge || badge2 ? "font-semibold text-fg" : ""}`}>{children}</span>
      {badge2 ? (
        <span
          data-testid={badge2TestId}
          title={badge2Title}
          aria-hidden="true"
          className="text-[10px] text-on-accent font-bold mono px-1.5 min-w-[18px] text-center leading-[18px]"
          style={{ background: "rgb(var(--agent-needs-review))" }}
        >{badge2}</span>
      ) : null}
      {badge ? (
        <span data-testid="unread-badge" aria-hidden="true" className="text-[10px] bg-accent-gradient text-fg font-bold mono px-1.5 min-w-[18px] text-center leading-[18px]">{badge}</span>
      ) : null}
    </button>
  );
}

// Accounts that cannot answer. The deploy gate's Health Check account has no
// password and no login, so a DM to it is a message into a void -- it was in
// the list because it is a workspace member, which is true and not the point.
// It stays visible in Members: it exists, and hiding it there would be a
// different kind of dishonest.
export const canReceiveDMs = (u) => !u?.service_account;

export default function Sidebar({ channels, users, projects, selection, onSelect, onChannelsChanged, onOpenSearch, myRole = "member", feedbackCount = 0, feedbackAwaitingCount = 0, mobileOpen = false, onMobileClose }) {
  const dmTargets = (users || []).filter(canReceiveDMs);
  const { logout, user } = useAuth();
  const { workspaces, current, currentId, switchWorkspace, unread } = useWorkspace();

  // The workspace logo is a stored file like any other and needs its own token.
  const brandLogo = useFileUrl(current?.brand_logo_file_id || null);

  sidebarRenders += 1;
  // A render landing between press and release is the suspect. Recording what
  // moved on each one is how we tell which arrival did it.
  useEffect(() => {
    trace("sidebar:render", {
      n: sidebarRenders, channels: channels.length, users: (users || []).length,
      unreadChannels: Object.keys(unread?.channels || {}).length,
      unreadDms: Object.keys(unread?.dms || {}).length,
      feedbackCount, feedbackAwaitingCount,
    });
  }, [channels, users, unread, feedbackCount, feedbackAwaitingCount]);

  // The server is the gate; hiding these only spares a guest buttons that would 403.
  const canManageChannels = myRole === "owner" || myRole === "admin";
  const isGuest = myRole === "guest";

  const [newChan, setNewChan] = useState("");
  const [newChanOpen, setNewChanOpen] = useState(false);
  const [createWsOpen, setCreateWsOpen] = useState(false);
  const [joinWsOpen, setJoinWsOpen] = useState(false);

  const createChannel = async () => {
    if (!newChan.trim() || !currentId) return;
    try {
      await api.post("/channels", { name: newChan, workspace_id: currentId });
      setNewChan(""); setNewChanOpen(false);
      await onChannelsChanged();
      toast.success(`#${newChan}creat`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu a reușit");
    }
  };

  return (
    <aside
      // Capture, on the container: a click that fires here but never on the row
      // is the gesture coming apart — press and release on different elements
      // deliver the click to their nearest common ancestor, which is this.
      onClickCapture={(e) => trace("nav:click-capture", {
        target: e.target?.closest?.("[data-testid]")?.dataset?.testid || e.target?.tagName || null,
        current: "aside",
      })}
      onPointerCancel={(e) => trace("nav:pointercancel", {
        target: e.target?.closest?.("[data-testid]")?.dataset?.testid || null,
      })}
      data-testid="workspace-sidebar"
      className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] flex-shrink-0 bg-surface flex flex-col h-screen transform transition-transform duration-200 md:relative md:z-auto md:w-64 md:max-w-none md:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
    >
      {/* Header with workspace switcher */}
      <div className="flex items-stretch border-b border-line/10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="workspace-switcher-trigger"
              className="flex-1 min-w-0 h-14 px-4 flex items-center gap-2 hover:bg-fg/5 transition-colors text-left"
            >
              {brandLogo ? (
                <img
                  src={brandLogo}
                  alt={brandName(current)}
                  data-testid="brand-logo"
                  className="w-7 h-7 object-contain flex-shrink-0"
                />
              ) : (
                <QedaraLogo variant="icon" markSize={28} className="flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold tracking-tight truncate">{current?.name || brandName(current)}</div>
                <div className="text-[10px] text-fg/40 mono truncate">{current?.slug || "team"}</div>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-fg/40" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-surface border-line/10 text-fg rounded-none w-64" align="start">
            {workspaces.map((w) => (
              <DropdownMenuItem
                key={w.id}
                data-testid={`workspace-option-${w.id}`}
                onClick={() => switchWorkspace(w.id)}
                className="flex items-center gap-2 cursor-pointer focus:bg-fg/10"
              >
                <div className="w-5 h-5 bg-brand/30 flex items-center justify-center text-[10px] mono">{w.name[0]?.toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{w.name}</div>
                  <div className="text-[10px] text-fg/40 mono">{w.role}</div>
                </div>
                {w.id === currentId && <Check className="w-3.5 h-3.5 text-brand" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="bg-fg/10" />
            <DropdownMenuItem
              data-testid="workspace-create-trigger"
              onClick={() => setCreateWsOpen(true)}
              className="cursor-pointer focus:bg-fg/10"
            >
              <Plus className="w-3.5 h-3.5 mr-2" /> Creează workspace
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="workspace-join-trigger"
              onClick={() => setJoinWsOpen(true)}
              className="cursor-pointer focus:bg-fg/10"
            >
              <Plus className="w-3.5 h-3.5 mr-2" /> Intră cu un cod
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          aria-label="Caută (Cmd+K)"
          title="Cmd+K"
          data-testid="sidebar-search-button"
          onClick={onOpenSearch}
          className="h-14 px-3 text-fg/50 hover:text-fg hover:bg-fg/5 transition-colors"
        >
          <Search className="w-4 h-4" />
        </button>
        <button
          type="button"
          aria-label="Închide navigarea"
          data-testid="mobile-sidebar-close"
          onClick={onMobileClose}
          className="md:hidden h-14 px-3 text-fg/50 hover:text-fg"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <CreateWorkspaceDialog open={createWsOpen} onOpenChange={setCreateWsOpen} />
      <JoinWorkspaceDialog open={joinWsOpen} onOpenChange={setJoinWsOpen} />

      {/* Scroll */}
      <div className="flex-1 overflow-y-auto pb-4">
        <OnboardingChecklist
          workspaceId={currentId}
          canDismiss={myRole === "owner" || myRole === "admin"}
        />
        <Section label="// Canale" action={canManageChannels && (
          <Dialog open={newChanOpen} onOpenChange={setNewChanOpen}>
            <DialogTrigger asChild>
              <button data-testid="new-channel-trigger" className="text-fg/40 hover:text-fg">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </DialogTrigger>
            <DialogContent className="bg-surface border-line/10 text-fg rounded-none"
              description="Numește canalul nou și spune pe scurt la ce folosește.">
              <DialogHeader><DialogTitle className="font-bold tracking-tight">Canal nou</DialogTitle></DialogHeader>
              <input
                data-testid="new-channel-name-input"
                autoFocus className="input-flat"
                placeholder="e.g. design, backend"
                value={newChan}
                onChange={(e) => setNewChan(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createChannel()}
              />
              <DialogFooter>
                <button data-testid="create-channel-confirm-button" onClick={createChannel} className="btn-primary">Creează</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}>
          {channels.map((c) => (
            <NavItem
              key={c.id}
              testId={`channel-link-${c.name}`}
              active={selection.kind === "channel" && selection.id === c.id}
              onClick={() => onSelect({ kind: "channel", id: c.id })}
              icon={Hash}
              badge={unread?.channels?.[c.id]}
              ariaLabel={`Canalul ${c.name}${unread?.channels?.[c.id] ? `, ${unread.channels[c.id]} necitite` : ""}`}
            >
              {c.name}
            </NavItem>
          ))}
        </Section>

        <Section label="// Mesaje directe">
          {dmTargets.length === 0 && <p className="text-xs text-fg/30 px-2">Încă niciun coleg.</p>}
          {dmTargets.map((u) => (
            <NavItem
              key={u.id}
              testId={`dm-link-${u.id}`}
              active={selection.kind === "dm" && selection.id === u.id}
              onClick={() => onSelect({ kind: "dm", id: u.id })}
              iconNode={<Avatar user={u} size={20} decorative />}
              badge={unread?.dms?.[u.id]}
              ariaLabel={`Mesaj direct cu ${u.name}${unread?.dms?.[u.id] ? `, ${unread.dms[u.id]} necitite` : ""}`}
            >
              {u.name}
            </NavItem>
          ))}
        </Section>

        <Section label="// Workspace">
          <NavItem testId="nav-projects" active={selection.kind === "projects"} onClick={() => onSelect({ kind: "projects" })} icon={KanbanSquare} ariaLabel={`Proiecte, ${projects.length} în total`}>
            Proiecte ({projects.length})
          </NavItem>
          <NavItem testId="nav-boards" active={selection.kind === "boards" || selection.kind === "board"} onClick={() => onSelect({ kind: "boards" })} icon={LayoutGrid} ariaLabel="Board-uri">
            Board-uri
          </NavItem>
          <NavItem testId="nav-canvases" active={selection.kind === "canvases" || selection.kind === "canvas"} onClick={() => onSelect({ kind: "canvases" })} icon={PenTool} ariaLabel="Whiteboard-uri">
            Whiteboard-uri
          </NavItem>
          <NavItem testId="nav-documents" active={selection.kind === "documents"} onClick={() => onSelect({ kind: "documents" })} icon={FileText} ariaLabel="Documente">
            Documente
          </NavItem>
          <NavItem testId="nav-snippets" active={selection.kind === "snippets"} onClick={() => onSelect({ kind: "snippets" })} icon={Code2} ariaLabel="Fragmente de cod">
            Fragmente de cod
          </NavItem>
          <NavItem
            testId="nav-feedback"
            active={selection.kind === "feedback"}
            onClick={() => onSelect({ kind: "feedback" })}
            icon={Bug}
            badge={feedbackCount > 0 ? feedbackCount : null}
            badge2={feedbackAwaitingCount > 0 ? feedbackAwaitingCount : null}
            badge2TestId="feedback-awaiting-badge"
            badge2Title={`${feedbackAwaitingCount} tickete așteaptă confirmarea ta`}
            ariaLabel={`Registru de feedback${feedbackCount ? `, ${feedbackCount} active` : ""}${feedbackAwaitingCount ? `, ${feedbackAwaitingCount} așteaptă confirmarea` : ""}`}
          >
            Feedback
          </NavItem>
          <NavItem testId="nav-members" active={selection.kind === "members"} onClick={() => onSelect({ kind: "members" })} icon={Users} ariaLabel="Membri și setările workspace-ului">
            Membri și invitații
          </NavItem>
          {!isGuest && (
            <NavItem testId="nav-settings" active={selection.kind === "settings"} onClick={() => onSelect({ kind: "settings" })} icon={Settings} ariaLabel="Setările contului">
              Setări
            </NavItem>
          )}
        </Section>
      </div>

      <div className="border-t border-line/10 px-3 py-3 flex items-center gap-2">
        <Avatar user={user} size={32} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-sm font-medium truncate">{user.name}</span>
            {myRole !== "member" && (
              <span
                data-testid="sidebar-role-badge"
                className={`label-tiny px-1 py-px border uppercase flex-shrink-0 ${
                  myRole === "owner" ? "text-brand border-brand/40" : "text-fg-muted border-line/20"
                }`}
              >{roleLabel(myRole)}</span>
            )}
          </div>
          <div className="text-[10px] text-fg/40 mono truncate">{user.email}</div>
        </div>
        <button data-testid="logout-button" onClick={logout} className="text-fg/40 hover:text-fg" title="Ieși din cont" aria-label="Ieși din cont">
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </aside>
  );
}

function CreateWorkspaceDialog({ open, onOpenChange }) {
  const { createWorkspace } = useWorkspace();
  const [name, setName] = useState("");
  const submit = async () => {
    if (!name.trim()) return;
    try {
      await createWorkspace(name.trim());
      toast.success(`„${name}" a fost creat`);
      onOpenChange(false); setName("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu a reușit"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-line/10 text-fg rounded-none"
        description="Creează un spațiu de lucru nou, cu propriile canale și membri.">
        <DialogHeader><DialogTitle className="font-bold tracking-tight">Workspace nou</DialogTitle></DialogHeader>
        <input data-testid="ws-create-name-input" autoFocus className="input-flat" placeholder="Numele workspace-ului" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
        <DialogFooter><button data-testid="ws-create-confirm-button" onClick={submit} className="btn-primary">Creează</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JoinWorkspaceDialog({ open, onOpenChange }) {
  const { joinWorkspace } = useWorkspace();
  const [code, setCode] = useState("");
  const submit = async () => {
    if (!code.trim()) return;
    try {
      await joinWorkspace(code.trim());
      toast.success("Ai intrat");
      onOpenChange(false); setCode("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Cod greșit"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-line/10 text-fg rounded-none"
        description="Intră într-un spațiu de lucru existent folosind codul primit.">
        <DialogHeader><DialogTitle className="font-bold tracking-tight">Intră într-un workspace</DialogTitle></DialogHeader>
        <input data-testid="ws-join-code-input" autoFocus className="input-flat mono uppercase" placeholder="VIBE-XXXXXX" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && submit()} />
        <DialogFooter><button data-testid="ws-join-confirm-button" onClick={submit} className="btn-primary">Intră</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
